import React, { FC, useContext, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Heading6 } from '@blueprint/ui';
import { useRouter } from 'next/router';

import { SelectPropertyType } from './SelectPropertyType';
import { SelectBeds } from './SelectBeds';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectTransportMode } from './SelectTransportMode';
import { SearchContext, SearchPageType } from '../../../search/SearchContext';
import { CloseIconWithText } from '../../../search/common';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '../../../reducers/filters';
import { selectCurrentSortOrder } from '../../../reducers/listingsSortOrder';
import {
    HeaderWrapper,
    ModalCloseIconWrapper,
    UpdateButton,
    LocationFilterWrapper,
    ModalContentWrapper,
    FindHomeTextWrapper,
    StickyWrapperUpdateButton,
    FilterDivider
} from './SearchInitiationMobileModal.styles';
import { BuyRentTabs } from '../../../homepage/components';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';

import { SelectRadius } from './SelectRadius';
import {
    trackQuickSearchModalUpdateResults,
    trackQuickSearchModalClose
} from './SearchInitiationMobileModal.tracking';
import {
    SearchInitationWrapper,
    StyledButtonMobile,
    StyledButtonWrapperMobile,
    HousePricesLocationWrapper
} from '../SearchInitiation/SearchInitiation.styles';
import { getTranslator } from '../../i18n/getTranslator';
import searchTranslations from 'public/locales/en/search.json';
import { searchHousePrices, searchProperties } from 'search/helpers';

const t = getTranslator(searchTranslations);

const SEARCH_BUTTON_TEXT = t('filters.search');

type Props = {
    isForSaleSearch: boolean;
    isShowing: boolean;
    showRadius?: boolean;
    canTabBetweenTypes?: boolean;
    updateResultsText: string;
    headlineText?: string;
    close: () => void;
    searchType: Tab;
};

export const SearchInitiationMobileModal: FC<Props> = (props) => {
    const {
        isForSaleSearch,
        showRadius = true,
        close,
        canTabBetweenTypes = true,
        updateResultsText,
        headlineText,
        searchType
    } = props;
    const router = useRouter();
    const { state, dispatch } = useContext(SearchContext);
    const { userAlertId } = state;

    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const { selectedValue: locationTextValue } = useSelector(
        locationTextSelector
    );
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const filters = useSelector(filtersValuesSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const [tabSelected, setTabSelected] = useState<Tab>('for-sale');

    const isForSale = canTabBetweenTypes
        ? tabSelected === 'for-sale'
        : isForSaleSearch;
    const isHousePricesSearch = tabSelected === 'house-prices';

    const handleSearchProperties = () => {
        const searchSource = 'facets';
        searchProperties(
            userAlertId,
            router,
            dispatch,
            searchType as SearchPageType,
            searchSource,
            filters,
            currentSortOrder
        );

        close();
    };

    const headerText = isForSaleSearch
        ? 'Search homes for sale'
        : 'Search homes to rent';

    const handleSearchPropertiesWithTracking = (
        evt: MouseEvent<HTMLButtonElement>
    ) => {
        evt.preventDefault();
        evt.stopPropagation();

        handleSearchProperties();
        trackQuickSearchModalUpdateResults();
    };

    const handleSearchHousePrices = () => {
        const value = locationTextValue || locationValue;

        searchHousePrices(value, identifier);
        close();
    };

    return (
        <ModalContentWrapper>
            <HeaderWrapper>
                {!headlineText ? (
                    <Text isBold color="grey-slate">
                        {headerText}
                    </Text>
                ) : null}
                <ModalCloseIconWrapper>
                    <CloseIconWithText
                        onClick={() => {
                            close();
                            trackQuickSearchModalClose();
                        }}
                        testId="quicksearch-modal_close-button_icon-only"
                        text="Close"
                    />
                </ModalCloseIconWrapper>
            </HeaderWrapper>

            {canTabBetweenTypes ? (
                <FindHomeTextWrapper>
                    <Heading6>{headlineText || headerText}</Heading6>
                </FindHomeTextWrapper>
            ) : null}

            {canTabBetweenTypes ? (
                <BuyRentTabs 
                onPress={setTabSelected} 
                selected={tabSelected} 
                includeHousePrices
                />
            ) : null}

            {isHousePricesSearch ? (
                <SearchInitationWrapper hasExtraPaddingTop>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValue}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapperMobile>
                        <StyledButtonMobile
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="mobile-house-prices-search-button"
                            disabled={!(locationValue || locationTextValue)}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButtonMobile>
                    </StyledButtonWrapperMobile>
                </SearchInitationWrapper>
            ) : (
                <>
                    <LocationFilterWrapper>
                        <LocationSearchInput
                            id="filters-location_mobile_modal"
                            onLocationInputKeyDown={handleSearchProperties}
                            onlyDisplayRemoveIconWhenActive={false}
                            value={locationValue}
                        />
                    </LocationFilterWrapper>

                    <FilterDivider />

                    {isTravelTimeSearch ? (
                        <>
                            <SelectMaxTravelTime />
                            <FilterDivider />
                            <SelectTransportMode />{' '}
                        </>
                    ) : showRadius ? (
                        <SelectRadius />
                    ) : null}

                    <FilterDivider />

                    {isForSale ? <SelectPriceForSale /> : <SelectPriceToRent />}

                    <FilterDivider />

                    <SelectBeds />

                    <FilterDivider />

                    <SelectPropertyType />
                    <StickyWrapperUpdateButton>
                        <UpdateButton
                            isFullWidth
                            type="button"
                            onClick={handleSearchPropertiesWithTracking}
                            data-testid="update-results-button"
                            title="Update"
                        >
                            {updateResultsText}
                        </UpdateButton>
                    </StickyWrapperUpdateButton>
                </>
            )}
        </ModalContentWrapper>
    );
};
